import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { apiTokenRequest, getActiveAccount, loginRequest, msalInstance } from '../auth/msal';
import { config } from '../env';
import { ApiError, ClientSuppliedScopeError } from './errors';
import { decodeJwtExp } from './jwt';

/**
 * =============================================================================
 * The gateway API client — the ONE place this application talks to anything.
 *
 * CLAUDE.md rule 11 / architecture §2.4.4: the React app never touches
 * Postgres, SharePoint or a queue directly. Everything goes through the API,
 * because that is where authorisation actually happens and because it is the
 * Option B → C upgrade seam. Concretely that means:
 *
 *   - `fetch` appears in this file and nowhere else in src/. eslint.config.js
 *     enforces it (no-restricted-globals on fetch/XMLHttpRequest outside
 *     src/api/), so a stray call in a component fails lint, not review.
 *   - There is no SharePoint SDK, no database driver, no Service Bus or Storage
 *     Queue client in package.json. Document bytes arrive through the API
 *     (§2.4.4, "API -> SP: Document bytes"), never from a SAS URL the SPA
 *     constructs.
 *   - Every request carries an Entra bearer token. The gateway re-derives role
 *     and scope from it on each call; the SPA's own view of those claims is
 *     display material only (see src/auth/permissions.ts).
 * =============================================================================
 */

/** Query values a caller may legitimately send. */
export type QueryValue = string | number | boolean | null | undefined;
export type Query = Readonly<Record<string, QueryValue | readonly QueryValue[]>>;

/**
 * Parameter names that would amount to the client telling the server which
 * parties it is allowed to see. Rejected outright.
 *
 * `party_key` is deliberately absent — asking about a party you can already see
 * is a filter, not a scope claim, and the gateway still applies the user's
 * scope as a predicate on top of it. What is banned is anything that *asserts*
 * the boundary rather than narrowing inside it.
 */
const FORBIDDEN_PARAMS: readonly string[] = [
  'scope',
  'scopes',
  'scope_group',
  'scope_groups',
  'party_scope',
  'practice_scope',
  'visible_parties',
  'allowed_parties',
  'roles',
  'actor_roles',
  'effective_access',
  'as_user',
  'on_behalf_of',
];

function assertNoScopeParams(query: Query | undefined): void {
  if (!query) return;
  for (const name of Object.keys(query)) {
    if (FORBIDDEN_PARAMS.includes(name.toLowerCase())) {
      throw new ClientSuppliedScopeError(name);
    }
  }
}

function buildUrl(path: string, query: Query | undefined): string {
  assertNoScopeParams(query);

  const base = config.api_base_url;
  const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`);

  if (query) {
    for (const [name, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const entry of value) {
          if (entry === undefined || entry === null) continue;
          url.searchParams.append(name, String(entry));
        }
      } else {
        url.searchParams.set(name, String(value));
      }
    }
  }

  return url.toString();
}

/**
 * Acquire an access token for the gateway API.
 *
 * Silent first; on InteractionRequiredAuthError fall back to a redirect. The
 * redirect never resolves — the browser navigates away — so the promise is
 * rejected rather than left hanging, and the caller's in-flight request is
 * abandoned cleanly.
 */
async function acquireToken(): Promise<string> {
  const account = getActiveAccount();
  if (!account) {
    await msalInstance.loginRedirect(loginRequest);
    throw new ApiError({
      message: 'No signed-in account; redirecting to sign in.',
      status: 401,
      correlation_id: 'n/a',
    });
  }

  try {
    const result = await msalInstance.acquireTokenSilent(apiTokenRequest(account));
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [config.api_scope], account });
      throw new ApiError({
        message: 'Consent or re-authentication required; redirecting.',
        status: 401,
        correlation_id: 'n/a',
      });
    }
    throw error;
  }
}

export interface RequestOptions {
  readonly query?: Query;
  readonly signal?: AbortSignal;
  /** Extra headers. Authorization is set by the client and cannot be overridden. */
  readonly headers?: Readonly<Record<string, string>>;
}

interface ApiRequest extends RequestOptions {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly body?: unknown;
}

async function request<TResponse>(init: ApiRequest): Promise<TResponse> {
  const url = buildUrl(init.path, init.query);
  const token = await acquireToken();

  // A client-generated correlation id lets an audit_log row (NFR-07) and a
  // Log Analytics access record be tied back to a single user action. The
  // server is free to prefer its own; whichever comes back is what ApiError
  // reports.
  const correlationId = crypto.randomUUID();

  const headers: Record<string, string> = {
    ...init.headers,
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'x-correlation-id': correlationId,
  };

  let payload: BodyInit | undefined;
  if (init.body !== undefined) {
    if (init.body instanceof FormData || init.body instanceof Blob) {
      // Multipart upload (FR-19 intake). Content-Type is left to the browser so
      // the multipart boundary is generated correctly.
      payload = init.body;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(init.body);
    }
  }

  const response = await fetch(url, {
    method: init.method,
    headers,
    ...(payload === undefined ? {} : { body: payload }),
    ...(init.signal === undefined ? {} : { signal: init.signal }),
    // No cookies. Authorisation travels as a bearer token only, so there is no
    // ambient credential to be replayed cross-site.
    credentials: 'omit',
    mode: 'cors',
    cache: 'no-store',
  });

  const serverCorrelationId = response.headers.get('x-correlation-id') ?? correlationId;

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json') || contentType.includes('+json');
  // Response.json() is typed `any`; narrow it to unknown immediately so no
  // `any` escapes this module into the surfaces. text() already returns string,
  // which widens to unknown on its own — asserting there is redundant.
  const body: unknown = isJson ? ((await response.json()) as unknown) : await response.text();

  if (!response.ok) {
    throw new ApiError({
      message: problemTitle(body) ?? `${init.method} ${init.path} failed with ${response.status}`,
      status: response.status,
      correlation_id: serverCorrelationId,
      body,
      // CLM-65 — on a 401, record when the token we just sent expires so
      // problem.ts can tell "expired" from "rejected while still valid". The
      // decode is local and unverified; it chooses copy, never access.
      token_exp: response.status === 401 ? decodeJwtExp(token) : null,
    });
  }

  return body as TResponse;
}

/** Pull a human-readable message out of an RFC 9457 problem document. */
function problemTitle(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  for (const key of ['detail', 'title', 'message', 'error'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

/*
 * Verb helpers. Endpoint-specific wrappers (search, retrieve-contract, upload,
 * manage-clause, resolve-party, verify-field, acknowledge-notification) are
 * deliberately NOT written here: the endpoint contracts land in PR-09 with
 * @clm/shared-types, and the surfaces that call them are CLM-30 and later.
 * Adding speculative wrappers now would mean rewriting them against the real
 * contract a fortnight later.
 */

export function apiGet<TResponse>(path: string, options?: RequestOptions): Promise<TResponse> {
  return request<TResponse>({ method: 'GET', path, ...options });
}

export function apiPost<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>({ method: 'POST', path, body, ...options });
}

export function apiPut<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>({ method: 'PUT', path, body, ...options });
}

export function apiPatch<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>({ method: 'PATCH', path, body, ...options });
}

export function apiDelete<TResponse>(path: string, options?: RequestOptions): Promise<TResponse> {
  return request<TResponse>({ method: 'DELETE', path, ...options });
}
